import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { periodesAPI, evenementsAPI } from '../services/api';
import { iconeEvenement, couleurEvenement } from '../services/evenementsCategories';
// Lot 5.6 : la déduction « périodes -> état des cellules » a quitté ce fichier
// pour `services/planningPeriodes.js`. L'écran d'export en a besoin à son tour,
// et deux calculs pour une seule notion, c'est un calendrier et un PDF qui
// finissent par montrer deux plannings différents.
import { construireColonnes, construireCellStates, cleCellule } from '../services/planningPeriodes';

const Calendar = forwardRef(({ calendrier, config, placedEvents, categoriesEvenements, visibiliteMois, onPastilleClick, onDateClick, onCellStatesChange, onPeriodeChange, onDemanderConfirmation, onErreur }, ref) => {
    const [visibilite, setVisibiliteState] = useState({});
    const [cellStates, setCellStates] = useState({});
    const [isDragging, setIsDragging] = useState(false);
    const [dragMode, setDragMode] = useState(null);
    const [dragColIndex, setDragColIndex] = useState(null);
    const [modifiedCells, setModifiedCells] = useState(new Set());
    const [saving, setSaving] = useState(false);
    const [editMode, setEditModeState] = useState(false);
    const [memorizedType, setMemorizedType] = useState(null);
    const [tooltipEvent, setTooltipEvent] = useState(null);
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    const [contextMenu, setContextMenu] = useState(null);
    const calendarWrapperRef = useRef(null);
    const calendarGridRef = useRef(null);
    const saveTimeoutRef = useRef(null);
    const pendingChangesRef = useRef([]);
    const memorizedTypeRef = useRef(null);

    const typesJournees = config?.typesJournees || [
        { code: 'F', libelle: 'Formation', couleur: '#1e293b', couleur_texte: '#FFFFFF' },
        { code: 'E', libelle: 'Entreprise', couleur: '#94a3b8', couleur_texte: '#FFFFFF' },
        { code: 'S', libelle: 'Stage', couleur: '#7C3AED', couleur_texte: '#FFFFFF' }
    ];
    const typesCodes = typesJournees.map(t => t.code);

    useEffect(() => {
        if (typesCodes.length > 0 && memorizedType === null) {
            setMemorizedType(typesCodes[0]);
            memorizedTypeRef.current = typesCodes[0];
        }
    }, [typesCodes]);

    useImperativeHandle(ref, () => ({
        getGridRef: () => calendarGridRef.current,
        getCellStates: () => cellStates,
        setEditMode: (mode) => setEditModeState(mode),
        setVisibilite: (vis) => setVisibiliteState(vis),
        // Lot 5.3 : la grille ne relisait les périodes qu'au changement d'année ou de
        // département. Après la restauration d'une version, la base a changé mais pas
        // la configuration : sans ce point d'entrée, l'écran affichait encore les
        // cellules d'avant, et il fallait un F5 pour voir le résultat.
        reload: () => loadPeriodesFromDB()
    }));

    useEffect(() => {
        if (config?.anneeScolaireId && config?.departement?.id && config?.sousNiveaux) {
            loadPeriodesFromDB();
        }
    }, [config?.anneeScolaireId, config?.departement?.id]);

    useEffect(() => {
        if (onCellStatesChange) onCellStatesChange(cellStates);
    }, [cellStates]);

    const buildColonneMap = useCallback(() => construireColonnes(config), [config]);

    const findSousNiveauForCol = useCallback((colIndex) => {
        const colonneMap = buildColonneMap();
        if (colIndex >= colonneMap.length) return null;
        const col = colonneMap[colIndex];
        if (!config?.sousNiveaux || !config?.departement?.id) return null;

        const colCode = col.code.toUpperCase();
        const deptId = config.departement.id;

        const sousNiveauxDuDept = config.sousNiveaux.filter(s =>
            s.departement_id === deptId && s.niveau_id === col.niveauId
        );

        let found = sousNiveauxDuDept.find(s => s.code.toUpperCase() === colCode);
        if (found) return found;

        if (colCode === 'FI') {
            if (sousNiveauxDuDept.length === 1) return sousNiveauxDuDept[0];
        }

        return null;
    }, [config, buildColonneMap]);

    const loadPeriodesFromDB = async () => {
        try {
            const response = await periodesAPI.getAll(config.anneeScolaireId, {
                departementId: config.departement.id
            });
            setCellStates(construireCellStates(response.data.periodes, config));
        } catch (err) {
            console.error('Erreur chargement périodes:', err);
        }
    };

    const saveToDB = useCallback(async (date, colIndex, newState) => {
        if (!config?.anneeScolaireId || !config?.departement?.id) return;
        const colonneMap = buildColonneMap();
        if (colIndex >= colonneMap.length) return;
        const col = colonneMap[colIndex];
        const sousNiveau = findSousNiveauForCol(colIndex);
        if (!sousNiveau) {
            // Même famille d'échec silencieux que le `catch` plus bas : la cellule se
            // colorait à l'écran et n'était jamais enregistrée. Ce cas arrive quand la
            // colonne affichée ne correspond à aucun groupe en base — typiquement
            // après un renommage de groupe au Paramétrage.
            console.error('Aucun sous-niveau trouvé pour colonne', colIndex, col.code, col.niveauCode);
            if (onErreur) onErreur(
                `La colonne « ${col.label || col.code} » ne correspond à aucun groupe enregistré. `
                + 'Vérifiez Paramétrage → Niveaux, groupes & heures, puis rechargez la page.'
            );
            return;
        }

        try {
            const existing = await periodesAPI.getAll(config.anneeScolaireId, {
                departementId: config.departement.id,
                niveauId: col.niveauId,
                sousNiveauId: sousNiveau.id
            });

            for (const p of existing.data.periodes) {
                const pStart = new Date(p.date_debut).toISOString().split('T')[0];
                const pEnd = new Date(p.date_fin).toISOString().split('T')[0];
                if (date >= pStart && date <= pEnd) {
                    await periodesAPI.delete(p.id);
                }
            }

            if (typesCodes.includes(newState)) {
                await periodesAPI.create({
                    anneeScolaireId: config.anneeScolaireId,
                    departementId: config.departement.id,
                    niveauId: col.niveauId,
                    sousNiveauId: sousNiveau.id,
                    dateDebut: date,
                    dateFin: date,
                    type: newState
                });
            }
            if (onPeriodeChange) onPeriodeChange();
        } catch (err) {
            // ⚠️ Ne JAMAIS se contenter d'un console.error ici. C'est ce qui a masqué,
            // le 2026-08-08, un refus du serveur sur les types de journées : les
            // cellules peintes disparaissaient au rechargement sans qu'aucun message
            // n'apparaisse. Un module en production ne doit pas échouer en silence.
            console.error('Erreur sauvegarde:', err);
            if (onErreur) onErreur(err.response?.data?.error || err.message);
        }
    }, [config, buildColonneMap, findSousNiveauForCol, typesCodes, onPeriodeChange]);

    const flushPendingChanges = useCallback(() => {
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        const changes = [...pendingChangesRef.current];
        pendingChangesRef.current = [];
        if (changes.length === 0) return;
        setSaving(true);
        const saveAll = async () => {
            for (const c of changes) await saveToDB(c.date, c.colIndex, c.state);
            setSaving(false);
        };
        saveAll();
    }, [saveToDB]);

    const queueSave = useCallback((date, colIndex, state) => {
        pendingChangesRef.current.push({ date, colIndex, state });
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(flushPendingChanges, 800);
    }, [flushPendingChanges]);

    // ⚠️ PERTE DE DONNÉES CORRIGÉE LE 2026-08-08.
    // L'enregistrement des cellules peintes est différé de 800 ms, et ce minuteur
    // était le SEUL déclencheur. Quitter le module ou rafraîchir la page dans la
    // seconde détruisait le composant avec son minuteur : les dernières cellules
    // n'étaient jamais envoyées au serveur. Au retour, le calendrier « ne
    // correspondait pas » — il affichait la vérité de la base, et la base n'avait
    // jamais reçu la fin.
    //
    // Deux filets, parce qu'ils couvrent deux cas différents :
    //   - le démontage (changement d'onglet, navigation dans l'application) ;
    //   - `beforeunload` (F5, fermeture de l'onglet), où React ne démonte rien.
    // `keepalive` sur la requête de secours : un `fetch` normal est annulé quand la
    // page se ferme, ce qui reproduirait exactement le défaut qu'on corrige.
    // ⚠️ La fonction de vidage passe par une RÉFÉRENCE, et l'effet ne s'installe
    // qu'une fois (`[]`). Première version de ce correctif : l'effet dépendait de
    // `flushPendingChanges`, lui-même recalculé à chaque rendu (sa chaîne de
    // dépendances remonte à `onPeriodeChange`, passé en fonction anonyme par le
    // parent). Résultat : le nettoyage se rejouait à CHAQUE rendu et annulait le
    // minuteur de 800 ms avant qu'il n'aboutisse — le correctif cassait précisément
    // ce qu'il devait protéger. Un effet de nettoyage doit s'installer une fois.
    const flushRef = useRef(flushPendingChanges);
    useEffect(() => { flushRef.current = flushPendingChanges; });

    useEffect(() => {
        const avantFermeture = (e) => {
            if (pendingChangesRef.current.length === 0) return;
            flushRef.current();
            // Prévient plutôt que de perdre le travail en silence.
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', avantFermeture);
        return () => {
            window.removeEventListener('beforeunload', avantFermeture);
            flushRef.current();   // démontage : on vide la file avant de disparaître
        };
    }, []);

    if (!calendrier || calendrier.length === 0) {
        return <div className="alert alert-error">Aucun calendrier généré.</div>;
    }

    const colonnes = buildColonneMap();
    const isVisible = (key) => visibilite[key] !== false;

    const getNextState = (currentState) => {
        if (!currentState || currentState === '') return typesCodes[0] || '';
        const idx = typesCodes.indexOf(currentState);
        if (idx === -1) return typesCodes[0] || '';
        if (idx === typesCodes.length - 1) return '';
        return typesCodes[idx + 1];
    };

    const getCellKey = (date, ci) => cleCellule(date, ci);

    const getDateStr = (date) => {
        return typeof date === 'string' ? date.split('T')[0] : new Date(date).toISOString().split('T')[0];
    };

    const applyStateChange = (jour, ci, ns) => {
        setCellStates(prev => ({ ...prev, [getCellKey(jour.date, ci)]: ns }));
        queueSave(getDateStr(jour.date), ci, ns);
    };

    const handleMouseDown = (jour, ci, e) => {
        if (!editMode || jour.est_weekend || jour.est_ferie || e.button !== 0) return;
        e.preventDefault();

        const currentState = cellStates[getCellKey(jour.date, ci)] || '';
        const isEraser = !memorizedTypeRef.current || memorizedTypeRef.current === '';
        let newState;

        if (currentState === '') {
            if (isEraser) {
                newState = typesCodes[0] || '';
                memorizedTypeRef.current = newState;
                setMemorizedType(newState);
            } else {
                newState = memorizedTypeRef.current;
            }
        } else {
            if (isEraser) {
                newState = '';
            } else {
                newState = getNextState(currentState);
                memorizedTypeRef.current = newState;
                setMemorizedType(newState);
            }
        }

        setIsDragging(true);
        setDragMode(newState);
        setDragColIndex(ci);
        setModifiedCells(new Set([getCellKey(jour.date, ci)]));
        applyStateChange(jour, ci, newState);
    };

    const handleMouseEnter = (jour, ci) => {
        if (!isDragging || jour.est_weekend || jour.est_ferie || ci !== dragColIndex) return;
        const key = getCellKey(jour.date, ci);
        if (!modifiedCells.has(key)) {
            setModifiedCells(prev => new Set([...prev, key]));
            applyStateChange(jour, ci, dragMode);
        }
    };

    const handleMouseUp = () => {
        if (isDragging) {
            setIsDragging(false);
            setDragMode(null);
            setDragColIndex(null);
            setModifiedCells(new Set());
        }
    };

    useEffect(() => {
        window.addEventListener('mouseup', handleMouseUp);
        return () => window.removeEventListener('mouseup', handleMouseUp);
    }, [isDragging]);

    useEffect(() => {
        document.body.style.userSelect = isDragging ? 'none' : '';
    }, [isDragging]);

    useEffect(() => {
        const handleClick = () => setContextMenu(null);
        window.addEventListener('click', handleClick);
        return () => window.removeEventListener('click', handleClick);
    }, []);

    useEffect(() => {
        const wrapper = calendarWrapperRef.current;
        if (!wrapper) return;
        const handleWheel = (e) => {
            if (e.shiftKey) {
                e.preventDefault();
                wrapper.scrollLeft += e.deltaY;
            }
        };
        wrapper.addEventListener('wheel', handleWheel, { passive: false });
        return () => wrapper.removeEventListener('wheel', handleWheel);
    }, []);

    const handleDateClick = (e, jour) => {
        if (!editMode || jour.est_weekend || jour.est_ferie) return;
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const dateStr = getDateStr(jour.date);
        if (onDateClick) {
            onDateClick({ date: dateStr, x: rect.right + 5, y: rect.top, jourInfo: jour });
        }
    };

    const handleDateDragOver = (e, jour) => {
        if (jour.est_weekend || jour.est_ferie) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDateDrop = async (e, jour) => {
        e.preventDefault();
        if (jour.est_weekend || jour.est_ferie) return;
        try {
            const data = JSON.parse(e.dataTransfer.getData('application/json'));
            const dateStr = getDateStr(jour.date);
            if (data.type === 'new-event') {
                await evenementsAPI.createDate({
                    evenementId: data.eventId,
                    anneeScolaireId: config.anneeScolaireId,
                    dateDebut: dateStr
                });
            } else if (data.type === 'move-event') {
                await evenementsAPI.updateDate(data.dateId, { dateDebut: dateStr });
            }
            if (window._reloadEvents) await window._reloadEvents();
            if (window._reloadEventManager) window._reloadEventManager();
        } catch (err) {
            console.error('Erreur drop:', err);
        }
    };

    // Map événements par date
    const eventsMap = {};
    if (placedEvents) {
        placedEvents.forEach(evt => {
            if (evt.date_debut) {
                const dateStr = new Date(evt.date_debut).toISOString().split('T')[0];
                if (!eventsMap[dateStr]) eventsMap[dateStr] = [];
                eventsMap[dateStr].push(evt);
            }
        });
    }

    // Grouper par mois
    const moisGroupes = {};
    calendrier.forEach(jour => {
        const key = jour.nom_mois + '-' + jour.annee;
        if (!moisGroupes[key]) moisGroupes[key] = { nom: jour.nom_mois, annee: jour.annee, jours: [] };
        moisGroupes[key].jours.push(jour);
    });
    const moisArray = Object.values(moisGroupes);
    const maxJours = moisArray.reduce((m, x) => Math.max(m, x.jours.length), 0);

    // PA.0 (2026-10-28, Bertrand) : le calendrier tient dans la HAUTEUR de la fenêtre — un mois entier visible sans
    // défiler. Seule la hauteur MINIMALE des lignes de jour change (variable CSS --pl-ligne) : largeurs, glisser-peindre
    // et exports ne bougent pas ; une date chargée d'événements agrandit encore sa ligne. Plancher de lisibilité
    // (LIGNE_MIN) : sur un écran trop petit, on retrouve le défilement d'avant. Recalcul : fenêtre redimensionnée,
    // zone du haut repliée / dépliée (ResizeObserver), barre du mode édition.
    const LIGNE_MAX = 26, LIGNE_MIN = 16;   // 16 : un portable 1366 × 768 voit le mois entier ; le texte fait 10 px
    const [hauteurLigne, setHauteurLigne] = useState(LIGNE_MAX);
    useLayoutEffect(() => {
        const wrapper = calendarWrapperRef.current;
        if (!wrapper || !maxJours) return undefined;
        let defilant = wrapper.parentElement;   // l'ancêtre qui fait défiler la page (le conteneur d'AppLayout)
        while (defilant && !/(auto|scroll)/.test(getComputedStyle(defilant).overflowY)) defilant = defilant.parentElement;
        const ajuster = () => {
            const mois = wrapper.querySelector('.month-column'), grille = mois && mois.querySelector('.days-grid');
            if (!grille) return;
            const visible = defilant ? defilant.clientHeight : window.innerHeight;
            const haut = defilant ? wrapper.getBoundingClientRect().top - defilant.getBoundingClientRect().top + defilant.scrollTop
                                  : wrapper.getBoundingClientRect().top + window.scrollY;
            const entetes = grille.getBoundingClientRect().top - mois.getBoundingClientRect().top;
            const barre = wrapper.offsetHeight - wrapper.clientHeight;      // la barre de défilement horizontale
            const dispo = visible - haut - entetes - barre - 28;            // 28 : la marge basse de la page
            const ligne = Math.max(LIGNE_MIN, Math.min(LIGNE_MAX, Math.floor(dispo / maxJours) - 1));   // - 1 : l'écart de la grille
            setHauteurLigne(ligne);
        };
        ajuster();
        window.addEventListener('resize', ajuster);
        const ro = typeof ResizeObserver !== 'undefined' && defilant && defilant.firstElementChild ? new ResizeObserver(ajuster) : null;
        if (ro) ro.observe(defilant.firstElementChild);
        return () => { window.removeEventListener('resize', ajuster); if (ro) ro.disconnect(); };
    }, [maxJours, editMode, saving]);

    // Colonnes de base visibles (filtre global)
    const colonnesVisibles = colonnes.filter(c => isVisible(c.niveauCode + '-' + c.code));

    // ========== CORRECTION PRINCIPALE ==========
    // Fonction pour obtenir les colonnes visibles pour un mois spécifique
    const getColonnesVisiblesMois = useCallback((moisNom, moisAnnee) => {
        // Clé du mois (ex: "septembre-2024")
        const moisKey = moisNom.toLowerCase() + '-' + moisAnnee;

        // Lot 5.1 : 7 traces de debug retirées ici. Elles étaient marquées « à retirer
        // en production » et n'avaient jamais été retirées : cette fonction est
        // appelée pour CHACUN des 14 mois à chaque rendu, avec en plus une trace par
        // colonne — soit plusieurs centaines de lignes de console à chaque
        // rafraîchissement du calendrier.

        // Si pas de config de visibilité par mois, retourner toutes les colonnes visibles globalement
        if (!visibiliteMois || typeof visibiliteMois !== 'object' || Object.keys(visibiliteMois).length === 0) {
            return colonnesVisibles;
        }

        // Si pas de config spécifique pour ce mois, retourner toutes les colonnes
        const configMois = visibiliteMois[moisKey];
        if (!configMois || typeof configMois !== 'object') {
            return colonnesVisibles;
        }

        // Filtrer les colonnes selon la visibilité du mois
        return colonnesVisibles.filter(c => configMois[c.niveauCode + '-' + c.code] !== false);
    }, [colonnesVisibles, visibiliteMois]);

    // Fonction pour obtenir l'index global d'une colonne
    const getGlobalColIndex = (col) => {
        return colonnes.findIndex(c => c.niveauCode === col.niveauCode && c.code === col.code);
    };

    const getTypeInfo = (code) => typesJournees.find(t => t.code === code);

    const getCellClass = (jour, ci) => {
        if (jour.est_weekend) return 'weekend';
        if (jour.est_ferie) return 'holiday';
        const s = cellStates[getCellKey(jour.date, ci)];
        if (s && getTypeInfo(s)) return 'type-journee-cell';
        if (jour.est_vacances) return 'vacation';
        return '';
    };

    const getCellStyle = (jour, ci) => {
        if (jour.est_weekend || jour.est_ferie) return {};
        const s = cellStates[getCellKey(jour.date, ci)];
        if (!s) return {};
        const typeInfo = getTypeInfo(s);
        if (!typeInfo) return {};
        return {
            background: typeInfo.couleur,
            color: typeInfo.couleur_texte || '#FFFFFF',
            fontWeight: 700
        };
    };

    const getCellText = (jour, ci) => {
        if (jour.est_weekend || jour.est_ferie) return '';
        return cellStates[getCellKey(jour.date, ci)] || '';
    };

    const getMemorizedLabel = () => {
        if (!memorizedType || memorizedType === '') return 'Effaceur';
        const info = getTypeInfo(memorizedType);
        return info ? info.code + ' — ' + info.libelle : memorizedType;
    };

    const getMemorizedStyle = () => {
        if (!memorizedType || memorizedType === '') {
            return { background: '#fee2e2', color: '#991b1b', borderColor: '#ef4444' };
        }
        const info = getTypeInfo(memorizedType);
        if (!info) return {};
        return { background: info.couleur + '20', color: info.couleur, borderColor: info.couleur };
    };

    return (
        <div>
            {/* Barre de statut compacte */}
            {(saving || editMode) && (
                <div className="calendar-status-bar">
                    {editMode && (
                        <div className="memorized-type-indicator" style={getMemorizedStyle()}>
                            Actif : <strong>{getMemorizedLabel()}</strong>
                        </div>
                    )}
                    {editMode && (
                        <div className="calendar-edit-hints">
                            <span><i className="fa-solid fa-arrow-pointer" /> <strong>Clic vide</strong> = dépose</span>
                            <span><i className="fa-solid fa-rotate" /> <strong>Clic lettre</strong> = change + mémorise</span>
                            <span><i className="fa-solid fa-up-down" /> <strong>Glisser</strong> = répète</span>
                            <span><i className="fa-solid fa-calendar-day" /> <strong>Clic date</strong> = événement</span>
                            {/* Lot 5.5 : dernier emoji du module (`⌨️`), devenu `fa-keyboard`. */}
                            <span><i className="fa-solid fa-keyboard" /> <strong>Shift + molette</strong> = défiler</span>
                        </div>
                    )}
                    {saving && <span className="saving-indicator"><i className="fa-solid fa-floppy-disk" /> Sauvegarde…</span>}
                </div>
            )}

			{/* Grille calendrier */}
			<div className={'calendar-wrapper' + (hauteurLigne < LIGNE_MAX ? ' cal-ajuste' : '')} ref={calendarWrapperRef} onMouseUp={handleMouseUp}
			     style={{ '--pl-ligne': hauteurLigne + 'px' }}>
				<div 
					className="calendar-grid" 
					ref={calendarGridRef}
					style={{
						display: 'flex',
						flexWrap: 'nowrap',
						gap: '8px',
						width: 'max-content'
					}}
				>
{moisArray.map((mois, idx) => {
    const colsMois = getColonnesVisiblesMois(mois.nom, mois.annee);
    const nbColsMois = colsMois.length;
    
    // Toujours au moins J + N° (même si pas de colonnes de groupes)
    const gridColumns = `30px 30px${nbColsMois > 0 ? ` repeat(${nbColsMois}, 60px)` : ''}`;

    return (
        <div 
            key={idx} 
            className="month-column"
            style={{
                flexShrink: 0,
                flexGrow: 0,
                width: 'auto',
                minWidth: 'min-content'
            }}
        >
            <div className="month-header">{mois.nom} {mois.annee}</div>
            
            {/* Headers - toujours afficher J et N° */}
            <div
                className="column-headers"
                style={{ 
                    display: 'grid', 
                    gridTemplateColumns: gridColumns,
                    gap: '1px'
                }}
            >
                <div className="col-header">J</div>
                <div className="col-header">N°</div>
                {colsMois.map((col, i) => (
                    <div
                        key={i}
                        className="col-header col-header-groupe"
                        style={{ background: col.couleur, color: 'white' }}
                        title={col.label}
                    >
                        {col.label.length > 8 ? col.label.substring(0, 7) + '…' : col.label}
                    </div>
                ))}
            </div>
            
            {/* Grille des jours - toujours afficher */}
            <div
                className="days-grid"
                style={{ 
                    display: 'grid', 
                    gridTemplateColumns: gridColumns,
                    gap: '1px'
                }}
            >
                {mois.jours.map((jour, jIdx) => {
                    const dateStr = getDateStr(jour.date);
                    const dateEvents = eventsMap[dateStr] || [];
                    const hasEvents = dateEvents.length > 0;

                    return (
                        <React.Fragment key={jIdx}>
                            {/* Colonne J */}
                            <div className={
                                'day-cell day-letter ' + 
                                (jour.est_ferie ? 'holiday ' : '') + 
                                (jour.est_vacances ? 'vacation' : '')
                            }>
                                {jour.jour_semaine_lettre}
                            </div>

                            {/* Colonne N° */}
                            <div
                                className={
                                    'day-cell day-number ' +
                                    (jour.est_ferie ? 'holiday ' : '') +
                                    (jour.est_vacances ? 'vacation ' : '') +
                                    (hasEvents ? 'has-events ' : '') +
                                    (editMode && !jour.est_weekend && !jour.est_ferie ? 'date-clickable ' : '')
                                }
                                onClick={(e) => handleDateClick(e, jour)}
                                onDragOver={(e) => handleDateDragOver(e, jour)}
                                onDrop={(e) => handleDateDrop(e, jour)}
                            >
                                <span className="day-number-text">{jour.numero_jour}</span>
                                {hasEvents && (
                                    <div className="event-pastilles">
                                        {dateEvents.map((evt, eIdx) => (
                                            <div
                                                key={eIdx}
                                                className="event-pastille"
                                                // Lot 5.4 ter : la couleur passe par la cascade
                                                // (dérogation de l'événement -> sa catégorie). Le
                                                // repli en dur `evt.couleur || '#1E293B'` aurait
                                                // fait virer au gris TOUTES les pastilles le jour
                                                // où la v36 a remis les couleurs machine à NULL.
                                                style={{ background: couleurEvenement(evt, categoriesEvenements) }}
                                                title={evt.libelle}
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    if (onPastilleClick) onPastilleClick(evt.id); 
                                                }}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Colonnes des groupes */}
                            {colsMois.map((col, cIdx) => {
                                const globalIdx = getGlobalColIndex(col);
                                
                                return (
                                    <div
                                        key={cIdx}
                                        className={
                                            'day-cell interactive-cell ' +
                                            getCellClass(jour, globalIdx)
                                        }
                                        style={getCellStyle(jour, globalIdx)}
                                        onMouseDown={(e) => handleMouseDown(jour, globalIdx, e)}
                                        onMouseEnter={() => handleMouseEnter(jour, globalIdx)}
                                    >
                                        {getCellText(jour, globalIdx)}
                                    </div>
                                );
                            })}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
})}
				</div>
			</div>

            {/* Tooltip */}
            {tooltipEvent && (
                <div className="event-tooltip" style={{ left: tooltipPos.x + 15, top: tooltipPos.y - 10 }}>
                    <span className="event-tooltip-icon"><i className={'fa-solid ' + iconeEvenement(tooltipEvent, categoriesEvenements)} /></span>
                    <span className="event-tooltip-text">{tooltipEvent.libelle}</span>
                </div>
            )}

            {/* Context menu */}
            {contextMenu && (
                <div className="event-context-menu" style={{ left: contextMenu.x, top: contextMenu.y }}>
                    <div className="context-menu-item" onClick={() => {
                        const evt = contextMenu.event;
                        setContextMenu(null);
                        // Lot 5.2 : window.prompt supprimé (la charte interdit
                        // alert/confirm/prompt). La modale du module porte le champ.
                        onDemanderConfirmation({
                            titre: 'Renommer un événement',
                            message: <>Nouveau libellé de l'événement <strong>{evt.libelle}</strong> :</>,
                            libelleConfirmer: 'Renommer',
                            saisie: { label: 'Libellé', valeurInitiale: evt.libelle, placeholder: "ex. Jury de fin d'année" },
                            action: async (valeur) => {
                                await evenementsAPI.updateConfig(evt.id, { libelle: valeur });
                                if (window._reloadEvents) window._reloadEvents();
                                if (window._reloadEventManager) window._reloadEventManager();
                            }
                        });
                    }}><i className="fa-solid fa-pen" /> Renommer</div>
                    <div className="context-menu-item" onClick={async () => {
                        await evenementsAPI.deleteDate(contextMenu.event.date_id);
                        if (window._reloadEvents) window._reloadEvents();
                        if (window._reloadEventManager) window._reloadEventManager();
                        setContextMenu(null);
                    }}><i className="fa-solid fa-link-slash" /> Dissocier</div>
                    <div className="context-menu-item context-menu-danger" onClick={() => {
                        const evt = contextMenu.event;
                        setContextMenu(null);
                        onDemanderConfirmation({
                            titre: 'Supprimer un événement',
                            message: <>Supprimer définitivement <strong>{evt.libelle}</strong> ? L'événement type disparaît du département, avec toutes ses dates.</>,
                            libelleConfirmer: 'Supprimer',
                            danger: true,
                            action: async () => {
                                await evenementsAPI.deleteConfig(evt.id);
                                if (window._reloadEvents) window._reloadEvents();
                                if (window._reloadEventManager) window._reloadEventManager();
                            }
                        });
                    }}><i className="fa-solid fa-trash" /> Supprimer</div>
                </div>
            )}
        </div>
    );
});

export default Calendar;